import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["id"];

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

import React, { useEffect, useMemo, useContext, useReducer } from 'react';
import { hideModal, removeModal, showModal } from './actions';
import { callbacks } from './callbacks';
import { components } from './components';
import { getModalId } from './id';

var _open = _async(function (modal, args) {
  var id = getModalId(modal);

  if (typeof modal !== 'string') {
    register(id, modal);
  }

  dispatch(showModal(id, args));

  if (!callbacks[id]) {
    var resolve;
    var promise = new Promise(function (r) {
      resolve = r;
    });
    callbacks[id] = {
      resolve: resolve,
      promise: promise
    };
  }

  return callbacks[id].promise;
});

var initialState = {};
var UnmodalContext = /*#__PURE__*/React.createContext(initialState);
var UnmodalIdContext = /*#__PURE__*/React.createContext('');

var dispatch = function dispatch(action) {
  throw new Error("\"" + action.type + "\" action must be used within the Unmodal.Provider");
};

function reducer(state, action) {
  switch (action.type) {
    case 'unmodal/open':
      {
        var _extends2;

        var _action$payload = action.payload,
            _id = _action$payload.id,
            args = _action$payload.args;
        var currentState = state[_id];
        return _extends({}, state, (_extends2 = {}, _extends2[_id] = _extends({}, currentState, {
          args: args,
          isOpen: true
        }), _extends2));
      }

    case 'unmodal/close':
      {
        var _extends3;

        var _id2 = action.payload.id;
        var _currentState = state[_id2];
        return _extends({}, state, (_extends3 = {}, _extends3[_id2] = _extends({}, _currentState, {
          isOpen: false
        }), _extends3));
      }

    case 'unmodal/remove':
      {
        var _id3 = action.payload.id;

        var newState = _extends({}, state);

        delete newState[_id3];
        return newState;
      }

    /* istanbul ignore next */

    default:
      throw new Error("Unhandled action: " + action);
  }
}

function _close(modal) {
  var id = getModalId(modal);
  dispatch(hideModal(id));
}

function _remove(id) {
  dispatch(removeModal(id));
  delete callbacks[id];
}

export var useModal = function useModal() {
  var modal = arguments.length <= 0 ? undefined : arguments[0];
  var args = arguments.length <= 1 ? undefined : arguments[1];
  var modals = useContext(UnmodalContext);
  var contextModalId = useContext(UnmodalIdContext);
  var id = modal ? getModalId(modal) : contextModalId;

  if (!id) {
    throw new Error('useModal is missing the modal ID.');
  }

  useEffect(function () {
    if (modal && typeof modal !== 'string') {
      register(id, modal);
    }
  }, [id, modal, args]);
  var modalInfo = modals[id];
  return useMemo(function () {
    return {
      id: id,
      args: modalInfo == null ? void 0 : modalInfo.args,
      isOpen: !!(modalInfo != null && modalInfo.isOpen),
      open: function open(args) {
        return _open(id, args);
      },
      close: function close() {
        return _close(id);
      },
      remove: function remove() {
        return _remove(id);
      },
      resolve: function resolve(args) {
        var _callbacks$id;

        (_callbacks$id = callbacks[id]) == null ? void 0 : _callbacks$id.resolve(args);
        delete callbacks[id];
      }
    };
  }, [id, modalInfo]);
};

function create(Comp) {
  return function (_ref) {
    var id = _ref.id,
        props = _objectWithoutPropertiesLoose(_ref, _excluded);

    var _useModal = useModal(id),
        args = _useModal.args;

    var modals = useContext(UnmodalContext);
    var modal = modals[id];

    if (!modal) {
      return null;
    }

    return /*#__PURE__*/React.createElement(UnmodalIdContext.Provider, {
      value: id
    }, /*#__PURE__*/React.createElement(Comp, _extends({}, props, args)));
  };
}

function register(id, comp) {
  if (!components[id]) {
    components[id] = comp;
  }
}

function unregister(id) {
  delete components[id];
}

function UnmodalContainer() {
  var modals = useContext(UnmodalContext);
  var renderedModals = Object.keys(modals).filter(function (id) {
    return !!modals[id] && components[id];
  }).map(function (id) {
    var _modals$id;

    return {
      id: id,
      args: ((_modals$id = modals[id]) == null ? void 0 : _modals$id.args) || {},
      Component: components[id]
    };
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderedModals.map(function (_ref2) {
    var id = _ref2.id,
        Component = _ref2.Component,
        args = _ref2.args;
    return /*#__PURE__*/React.createElement(Component, _extends({
      key: id,
      id: id
    }, args));
  }));
}

function Provider(_ref3) {
  var children = _ref3.children;

  var _useReducer = useReducer(reducer, initialState),
      state = _useReducer[0],
      dispatcher = _useReducer[1];

  dispatch = dispatcher;
  return /*#__PURE__*/React.createElement(UnmodalContext.Provider, {
    value: state
  }, children, /*#__PURE__*/React.createElement(UnmodalContainer, null));
}

function Register(_ref4) {
  var id = _ref4.id,
      component = _ref4.component;
  useEffect(function () {
    register(id, component);
    return function () {
      unregister(id);
    };
  }, [id, component]);
  return null;
}

export var Unmodal = {
  Provider: Provider,
  Register: Register,
  create: create,
  open: _open,
  close: _close,
  register: register,
  unregister: unregister
};